import { StatusSwitch } from '@app/modules/admin/components/permission/StatusSwitch';
import {
  useUsersAuthzAPIAdminPermissionsWriteAllowPermissionToGroup,
  useUsersAuthzAPIAdminPermissionsWriteDenyPermissionToGroup,
  type GroupPermissionDto,
} from '@mi-front/api-clients';
import { Box, Card, CardContent } from '@mui/material';
import { useState } from 'react';
import { useLanguage } from '../../../../core/hooks/useLanguage';
import type { PermissionItemComponentProps } from './ActionPermissionsView';

interface Props extends PermissionItemComponentProps<GroupPermissionDto> {
  readonly groupId: string;
}

export function useLocalizedText(textMap: object) {
  const lang = useLanguage() as keyof typeof textMap;

  return textMap[lang];
}

export function GroupPermissionItem(p: Props) {
  const { groupId, permissionState: initialGroupPermission, permissionDefinition } = p;

  const [groupPermission, setGroupPermission] = useState(initialGroupPermission);

  const mutationParams = {
    groupId,
    permission: groupPermission.permission,
  };

  const allowMutation = useUsersAuthzAPIAdminPermissionsWriteAllowPermissionToGroup({
    mutation: {
      onSuccess: (data) => {
        setGroupPermission(data as GroupPermissionDto);
      },
    },
  });

  const denyMutation = useUsersAuthzAPIAdminPermissionsWriteDenyPermissionToGroup({
    mutation: {
      onSuccess: (data) => {
        setGroupPermission(data as GroupPermissionDto);
      },
    },
  });

  const isUpdating = allowMutation.isPending || denyMutation.isPending;

  return (
    <Card>
      <CardContent>
        <Box mb={1}>{useLocalizedText(permissionDefinition.description)}</Box>
        <Box sx={{ marginRight: 3 }}>
          <StatusSwitch
            checked={groupPermission.allowed}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              event.target.checked
                ? allowMutation.mutate(mutationParams)
                : denyMutation.mutate(mutationParams);
            }}
            disabled={isUpdating}
            sx={{ marginBottom: 0 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
